import { getAppConfig } from '@/config/AppConfig';
import { useMemo } from 'react';
import { auth0Client } from './authProvider';
import { useQuery } from '@tanstack/react-query';
import { BSON } from 'realm-web';
import { CustomPipelineOptions } from './dataProvider';

export const useBackendRPC = (
  backendRpcEndpoint: string,
  getRefreshedToken: () => Promise<string>,
) =>
  useMemo(
    () =>
      backendRpcClientFactory(backendRpcEndpoint, getRefreshedToken),
    [getRefreshedToken, backendRpcEndpoint],
  );

type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export const backendClient = async <T>(
  method: HTTPMethod,
  path: string,
  data: any,
  getRefreshedToken: () => Promise<string | null>,
) => {
  const token = await getRefreshedToken();

  if (!token) {
    console.warn('No token found');

    throw new Error('No token found');
  }

  const headers = token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};

  const body = method === 'GET' ? undefined : JSON.stringify(data);

  console.warn('backendRpcClientFactory', {
    method,
    path,
    body,
  });

  const response = await fetch(`${adminBackendUri}${path}`, {
    method,
    headers: headers as any,
    body,
  });

  console.warn('response', response.status);

  const result = (await response.json()) as T;

  console.warn('result', result);

  return result;
};

export const backendRpcClientFactory =
  <T>(
    backendRpcEndpoint: string,
    getRefreshedToken: () => Promise<string | null>,
  ) =>
  async (
    name: string,
    params: any[] = [],
    { allowAnonymous = false, tokenOverride }: any = {},
  ) => {
    console.warn('name', name);
    console.warn('params', params);
    console.warn('tokenOverride', tokenOverride);
    console.warn('allowAnonymous', allowAnonymous);

    const token = await (async () => {
      if (tokenOverride !== undefined) {
        return tokenOverride;
      }

      if (allowAnonymous) {
        return null;
      }

      return getRefreshedToken();
    })();

    if (!token && !allowAnonymous) {
      console.warn('No token found');

      throw new Error('No token found');
    }

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    const body = {
      name,
      params,
    };

    console.warn('backendRpcClientFactory', {
      name,
      params,
      backendRpcEndpoint,
      body,
    });

    const response = await fetch(`${backendRpcEndpoint}`, {
      method: 'POST',
      headers: headers as any,
      body: JSON.stringify(body),
    });

    console.warn('response', response.status);

    const result = await (async () => {
      try {
        return (await response.json()) as T;
      } catch (_err) {
        const text = await response.text();

        throw new Error(text);
      }
    })();

    console.warn('result', result);

    return result;
  };

export type BackendRpcClient = ReturnType<
  typeof backendRpcClientFactory
>;

const appConfig = getAppConfig();

const adminBackendUri = appConfig.adminBackendUri;

const ingestionEndpoint = `${adminBackendUri}/ingestion`;

const rpcEndpointUri = `${adminBackendUri}/rpc`;

export const adminRpcClient = backendRpcClientFactory(
  rpcEndpointUri,
  () => auth0Client.getTokenSilently(),
);

export const useRpcClientCall = (
  name: string,
  params: any[],
  options: any,
) =>
  useQuery({
    queryKey: ['rpc-client-call', name, ...params],
    queryFn: () => adminRpcClient(name, params, options),
  });

type AggregationOptions = {
  allowDiskUse: boolean;
};

export const aggregate = async <
  TOutput extends Record<string, any[]>,
>(
  collectionName: string,
  pipeline: any[] | Record<string, any[]>,
  options: AggregationOptions = { allowDiskUse: false },
) => {
  const pipelines = Array.isArray(pipeline)
    ? { [collectionName]: pipeline }
    : pipeline;

  const result = await backendClient<TOutput>(
    'POST',
    `/aggregate/${collectionName}`,
    {
      pipelines,
      options,
    },
    () => auth0Client.getTokenSilently(),
  );

  return result as any;
};

export const remoteCustomFetcher = async <TOutput>(
  collectionName: string,
  options: Omit<CustomPipelineOptions, 'projection'>,
) => {
  const result = await backendClient<TOutput>(
    'POST',
    `/list/${collectionName}`,
    options,
    () => auth0Client.getTokenSilently(),
  );

  return result as any;
};

const ensureString = (
  value: unknown,
  name: string,
): string => {
  if (typeof value === 'string') return value;
  // EJSON / MongoDB aggregation response: id can be { $oid: "hex" }
  if (
    value != null &&
    typeof value === 'object' &&
    '$oid' in value &&
    typeof (value as { $oid: string }).$oid === 'string'
  ) {
    return (value as { $oid: string }).$oid;
  }
  if (value != null && typeof (value as { toString?: () => string }).toString === 'function') {
    return (value as { toString: () => string }).toString();
  }
  if (value != null && typeof (value as { toHexString?: () => string }).toHexString === 'function') {
    return (value as { toHexString: () => string }).toHexString();
  }
  throw new Error(
    `powersyncIngestionClient: ${name} must be a string or have toString/toHexString or $oid, got ${typeof value}`,
  );
};

export const powersyncIngestionClient = async (
  collectionName: string,
  op: 'PUT' | 'PATCH',
  data: any,
  id = new BSON.ObjectId().toString(),
) => {
  const token = await auth0Client.getTokenSilently();

  const typeStr = ensureString(collectionName, 'collectionName');
  if (id === undefined || id === null) {
    throw new Error('powersyncIngestionClient: id is required for PATCH/PUT');
  }
  const idStr = ensureString(id, 'id');

  const body = {
    batch: [
      {
        op,
        type: typeStr,
        id: idStr,
        data,
      },
    ],
  };

  const response = await fetch(ingestionEndpoint, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const json = await response.json();

  return {
    status: response.status,
    data: body,
    json,
  };
};

export const getOne = async (
  resource: string,
  id: string,
  raw = false,
) => {
  const pipeline = [
    {
      $match: {
        _id: raw ? id : { $oid: id },
      },
    },
    {
      $limit: 1,
    },
  ];

  const response: any = await aggregate(resource, {
    results: pipeline,
  });

  return response?.results?.[0];
};

export const getToken = () => auth0Client.getTokenSilently();
